import { useState } from "react";
import { batch } from "react-redux";
import { uniq } from "underscore";

import { LanguagesAbbr, Session } from "../../../../../../../../types/working-model";
import { useAppDispatch, useTypedSelector } from "../../../../../../../../store/reducers/use-typed-selector";
import ModalComponent from "@general-ui/modal/modal";
import languageLabelMap from '../../../../../../../../utils/translate-langs-map.json';
import GradientButton from "@general-ui/button/gradient-button";
import WaitingIndicator from "@general-ui/waiting-indicator/waiting-indicator";
import { showAlertLong } from "@general-ui/alert/alert-service";
import { updateSession, updateWorkingSession } from "store/actions/admin/create-event/session";
import { sessionLanguagesUpdated, setEventLanguages, updateMultipleSessions, updateSessionInfo } from "store/actions/admin/create-event";
import { OptionalComponent } from "utils/optional-component";

import './add-language-modal.scss';

interface IAddLanguageConfirmationModal {
	languageToAdd: LanguagesAbbr | null;
	handleClose: () => void;
}

function AddLanguageConfirmationModal({ languageToAdd, handleClose }: IAddLanguageConfirmationModal): JSX.Element {
	const dispatch = useAppDispatch();

	const token = useTypedSelector(state => state.AuthReducer.token);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);
	const eventLanguages = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings.i18n?.event_languages);

	const [saving, setSaving] = useState(false);

	const handleSave = (sessionsToApply: 'single' | 'all') => {
		if (!workingEvent || !workingSession || !languageToAdd || !token || !eventLanguages) {
			return;
		}

		try {
			setSaving(true);

			const updatedWorkingSession = {
				...workingSession,
				languages: [...workingSession.languages, languageToAdd]
			};

			batch(() => {
				if (sessionsToApply === 'single') {
					dispatch(updateSession(updatedWorkingSession, token));
				}
				dispatch(updateWorkingSession(updatedWorkingSession));
				dispatch(sessionLanguagesUpdated(workingSession.session, updatedWorkingSession.languages as LanguagesAbbr[]));

				if (sessionsToApply === 'all') {
					// Add the new language to every session
					const updatedWorkingEventSessions = workingEvent.sessions.reduce((acc: Session[], cur: Session) => {
						// Don't bother an update if that session already has the new language
						if (cur.languages.includes(languageToAdd)) return acc;

						return [
							...acc,
							{
								...cur,
								languages: [...cur.languages, languageToAdd]
							}
						];
					}, []);

					dispatch(updateMultipleSessions(updatedWorkingEventSessions, token));
				}

				if (eventLanguages && !eventLanguages.includes(languageToAdd)) {
					dispatch(setEventLanguages([...eventLanguages, languageToAdd]));
				}
			});
		} catch (e) {
			console.error(e);
			showAlertLong({
				message: 'Unable to add language',
				description: 'Please try again, or contact support if this persists',
				type: 'error'
			});
		} finally {
			setSaving(false);
			handleClose();
		}
	};

	return (
		<ModalComponent
			/* @ts-expect-error json import */
			title={`Add ${languageLabelMap[languageToAdd]} to all sessions?`}
			className="add-language-modal"
			open={!!languageToAdd}
			padTitle={false}
			onRequestClose={handleClose}
			closeable
			cancellable
			footer={
				<>
					<button className="secondary" onClick={() => handleSave('single')}>
						No, just to this session
					</button>
					<GradientButton
						fullBackgroundGradient
						color="black"
						gradient="--gradient-sunrise"
						buttonProps={{
							onClick: () => handleSave('all'),
							disabled: saving
						}}
					>
						{saving ? <WaitingIndicator /> : 'Apply to all'}
					</GradientButton>
				</>
			}
		>
			<div>You can always turn it off for each session</div>
			<OptionalComponent display={!!(workingEvent?.sessions.length && workingEvent?.sessions.length > 5)}>
				<div>Since this project has more than 5 sessions, this process can take some time.</div>
			</OptionalComponent>
		</ModalComponent>
	);
}

export default AddLanguageConfirmationModal;